/**
 * /ap — Vendor bills (Purchases).
 *
 * Single-purpose page: lists AP invoices ("bills"), supports
 *   - Upload bill (Claude Vision PDF/photo intake → draft bill)
 *   - + New bill (manual entry)
 *   - Multi-select → "Pay selected" (routes to /payments/check-run wizard
 *     pre-loaded with the chosen invoices)
 *   - Multi-select → "Quick check run" (legacy in-place dialog)
 *
 * Split out of the old combined /invoices page (April 2026). The label
 * "Bills" is the user-facing term; internal model + queries remain
 * `apInvoicesListQueryOptions` etc.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@mui/material";

import { useShortcut } from "@/lib/shortcuts";
import { HelpHint } from "@/components/HelpHint";
import { UploadBillDialog } from "@/components/UploadBillDialog";
import { CheckRunDialog } from "@/components/CheckRunDialog";
import {
  apInvoicesListQueryOptions,
  partiesListQueryOptions,
} from "@/lib/queries";
import { InvoicesGrid } from "@/grids/InvoicesGrid";
import type { APInvoice, Party } from "@/lib/types";

interface CommonRow {
  id: string;
  party_id: string;
  party_name: string | null;
  outstanding: number;
  currency_code: string;
  status: string;
}

interface APBillsSearch {
  // Pre-set the status filter via the URL, e.g. /ap?filter=overdue. Lets the
  // agent deep-link "show me overdue bills" straight to the filtered grid.
  filter?: string;
}

export const Route = createFileRoute("/_auth/ap")({
  validateSearch: (s: Record<string, unknown>): APBillsSearch => ({
    filter: typeof s.filter === "string" ? s.filter : undefined,
  }),
  component: APBillsPage,
});

// Outstanding = unpaid balance on a bill, net of credit/debit memos.
const outstandingOf = (inv: APInvoice) =>
  Math.max(0, Number(inv.total) - Number(inv.memo_total) - Number(inv.paid_amount));

// Status filters for the bills grid. Dates are ISO (YYYY-MM-DD) so string
// comparison is correct. `today`/`weekOut` are passed in so the list reacts
// to the real current date.
const BILL_FILTERS: {
  key: string;
  label: string;
  test: (inv: APInvoice, today: string, weekOut: string) => boolean;
}[] = [
  { key: "all", label: "All bills", test: () => true },
  { key: "overdue", label: "Overdue", test: (inv, today) => outstandingOf(inv) > 0 && inv.due_date < today },
  { key: "due_soon", label: "Due soon", test: (inv, today, weekOut) => outstandingOf(inv) > 0 && inv.due_date >= today && inv.due_date <= weekOut },
  { key: "open", label: "Open", test: (inv) => outstandingOf(inv) > 0 },
  { key: "paid", label: "Paid", test: (inv) => outstandingOf(inv) <= 0 },
];

function APBillsPage() {
  const navigate = useNavigate();

  // `n` opens "New bill" — mirrors the header button.
  useShortcut("n", () => navigate({ to: "/invoices/ap/new" }), {
    label: "New bill",
  });

  const { filter: urlFilter } = Route.useSearch();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [apSelectedRows, setApSelectedRows] = useState<CommonRow[]>([]);
  const [checkRunOpen, setCheckRunOpen] = useState(false);
  const [billFilter, setBillFilter] = useState<string>(
    urlFilter && BILL_FILTERS.some((f) => f.key === urlFilter) ? urlFilter : "all",
  );

  const apInvoices = useQuery(apInvoicesListQueryOptions({}));
  const allParties = useQuery(partiesListQueryOptions());

  // Apply the status filter to the bills before they reach the grid.
  const today = new Date().toISOString().slice(0, 10);
  const weekOut = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  const allBills = useMemo(() => apInvoices.data ?? [], [apInvoices.data]);
  const counts = useMemo(
    () =>
      Object.fromEntries(
        BILL_FILTERS.map((f) => [f.key, allBills.filter((b) => f.test(b, today, weekOut)).length]),
      ) as Record<string, number>,
    [allBills, today, weekOut],
  );
  const filteredBills = useMemo(() => {
    const f = BILL_FILTERS.find((x) => x.key === billFilter) ?? BILL_FILTERS[0];
    return allBills.filter((b) => f.test(b, today, weekOut));
  }, [allBills, billFilter, today, weekOut]);

  const partiesById = useMemo(() => {
    const m: Record<string, Party> = {};
    (allParties.data ?? []).forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [allParties.data]);

  const fmtMoney = (n: number, ccy = "USD") =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: ccy,
      maximumFractionDigits: 2,
    }).format(n);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Bills</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Vendor bills · click bill # to open · pay outstanding bills
          </span>
          <HelpHint docPath="bills/overview.md" label="Bills help" />
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 border border-slate-300 dark:border-gray-700 px-2.5 py-1.5 rounded-md hover:bg-slate-50 dark:hover:bg-gray-800 font-medium"
            title="Drop a PDF or photo of a vendor bill — our AI extracts it"
          >
            Upload bill
          </button>
          <Link
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to={"/invoices/ap/new" as any}
            className="text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New bill
          </Link>
        </div>
      </div>

      {/* Selection action bar */}
      {apSelectedRows.length > 0 && (
        <div className="bg-violet-50 border-b border-violet-200 px-6 py-2 flex items-center gap-4 dark:bg-violet-950/30 dark:border-violet-800">
          <span className="text-sm font-medium text-violet-900 dark:text-violet-200">
            {apSelectedRows.length} bill{apSelectedRows.length !== 1 ? "s" : ""} selected
            {" · "}
            <span className="font-semibold">
              {fmtMoney(
                apSelectedRows.reduce((s, r) => s + r.outstanding, 0),
                apSelectedRows[0]?.currency_code ?? "CAD",
              )}{" "}
              outstanding
            </span>
            {" · "}
            {new Set(apSelectedRows.map((r) => r.party_id)).size} vendor
            {new Set(apSelectedRows.map((r) => r.party_id)).size !== 1 ? "s" : ""}
          </span>
          <div className="flex-1" />
          <Button
            variant="contained"
            size="small"
            onClick={() =>
              navigate({
                to: "/payments/check-run",
                search: { invoices: apSelectedRows.map((r) => r.id).join(",") },
              })
            }
          >
            Pay selected
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<span>🖨</span>}
            onClick={() => setCheckRunOpen(true)}
          >
            Quick check run
          </Button>
          <Button variant="outlined" size="small" onClick={() => setApSelectedRows([])}>
            Clear
          </Button>
        </div>
      )}

      <UploadBillDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />

      {checkRunOpen && (
        <CheckRunDialog
          open={checkRunOpen}
          onClose={() => {
            setCheckRunOpen(false);
            setApSelectedRows([]);
          }}
          invoices={(() => {
            const ids = new Set(apSelectedRows.map((r) => r.id));
            return (apInvoices.data ?? []).filter((inv: APInvoice) => ids.has(inv.id));
          })()}
          partiesById={partiesById}
        />
      )}

      {/* Status filter bar */}
      <div className="flex items-center gap-2 px-6 pt-3 flex-wrap">
        {BILL_FILTERS.map((f) => {
          const active = billFilter === f.key;
          const isOverdue = f.key === "overdue" && counts[f.key] > 0;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setBillFilter(f.key)}
              className={
                "flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors " +
                (active
                  ? "border-blue-500 bg-blue-600 text-white"
                  : isOverdue
                    ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-300 dark:hover:bg-gray-800")
              }
            >
              {f.label}
              <span
                className={
                  "rounded-full px-1.5 text-xs " +
                  (active ? "bg-white/25" : "bg-slate-100 dark:bg-gray-800")
                }
              >
                {counts[f.key] ?? 0}
              </span>
            </button>
          );
        })}
        <span className="ml-1 text-xs text-slate-400">
          Showing {filteredBills.length} of {allBills.length} bills
        </span>
      </div>

      <div className="flex-1 px-6 py-3 overflow-hidden">
        <InvoicesGrid
          direction="ap"
          invoices={filteredBills}
          partiesById={partiesById}
          loading={apInvoices.isLoading || allParties.isLoading}
          onSelectionChange={(rows) => setApSelectedRows(rows as unknown as CommonRow[])}
        />
      </div>
    </div>
  );
}
